import type { ScmConnectionTestResult } from '@/api/schemas';
import { cn } from '@/lib/utils';
import { Icon } from '@/components/Icon';
import { ProblemAlert } from '@/components/ProblemAlert';
import { Button } from '@/components/ui/button';
import { verifyRows, verifyVerdict, type VerifyTone } from './tokenGuideModel';
import type { VerifyScmToken } from './useVerifyScmToken';

const LOOK: Record<VerifyTone, { icon: string; tone: string }> = {
  passed: { icon: 'check_circle', tone: 'text-succeeded' },
  failed: { icon: 'error', tone: 'text-failed' },
  warning: { icon: 'warning', tone: 'text-awaiting' },
  skipped: { icon: 'do_not_disturb_on', tone: 'text-subtle' },
};

const PANEL: Record<VerifyTone, string> = {
  passed: 'border-succeeded/30 bg-succeeded/10',
  failed: 'border-failed/30 bg-failed/10',
  warning: 'border-awaiting/30 bg-awaiting/10',
  skipped: 'border-border bg-card',
};

/** What Bitbucket said about the token: a verdict, then one row per check. */
function VerifyResult({ result }: { result: ScmConnectionTestResult }) {
  const verdict = verifyVerdict(result);
  return (
    <div data-testid="scm-token-verify-result" data-ok={result.ok} data-tone={verdict.tone} role="status" className={cn('flex flex-col gap-2 rounded-lg border p-3 text-xs', PANEL[verdict.tone])}>
      <p className="flex items-start gap-1.5 font-semibold text-foreground">
        <Icon name={LOOK[verdict.tone].icon} size={16} fill className={cn('shrink-0', LOOK[verdict.tone].tone)} />
        {verdict.text}
      </p>
      <ul className="flex flex-col gap-1">
        {verifyRows(result).map((row) => (
          <li key={row.key} data-check={row.key} data-tone={row.tone} className="flex items-start gap-1.5">
            <Icon name={LOOK[row.tone].icon} size={14} className={cn('mt-px shrink-0', LOOK[row.tone].tone)} />
            <span className="min-w-0 text-muted-foreground">
              <span className="font-medium text-foreground">{row.label}</span>
              {row.message ? <span className="break-words"> · {row.message}</span> : null}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** "Verify token": asks Bitbucket what the typed token can do, before anything is stored. */
export function TokenVerify({ check }: { check: VerifyScmToken }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <Button type="button" size="sm" variant="outline" data-testid="scm-token-verify" disabled={!check.ready} loading={check.busy} onClick={() => void check.verify().catch(() => undefined)}>
          <Icon name="verified_user" size={16} />
          Verify token
        </Button>
        {!check.ready ? <span className="text-xs text-subtle">Fill in the fields above and paste the token first.</span> : null}
      </div>
      {check.result ? <VerifyResult result={check.result} /> : null}
      {check.error ? <ProblemAlert error={check.error} testId="scm-token-verify-problem" /> : null}
    </div>
  );
}
